

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { BookmarkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TechniqueDifficulty, TechniqueType } from '@/types/technique';
import { TechniqueCardBadges } from './technique-card/TechniqueCardBadges';
import { TechniqueCardImage } from './technique-card/TechniqueCardImage';
import { TechniqueCardFooter } from './technique-card/TechniqueCardFooter';
import { useTechniqueLikes } from '@/hooks/technique/useTechniqueLikes';
import { useTechniqueVisibility } from '@/hooks/technique/useTechniqueVisibility';
import { cn } from '@/lib/utils';

interface TechniqueCardProps {
  id: string;
  name: string;
  description: string;
  position?: string;
  level?: TechniqueDifficulty;
  type?: TechniqueType;
  imageUrl?: string;
  onSave?: (e: React.MouseEvent) => void;
  saved?: boolean;
  hasNotes?: boolean;
  hasVideos?: boolean;
  isEditable?: boolean;
  onEdit?: (e: React.MouseEvent) => void;
  isUserCreated?: boolean;
  isHidden?: boolean;
  onHideToggle?: (e: React.MouseEvent) => void;
  isLiked?: boolean;
  onLikeToggle?: (e: React.MouseEvent) => void;
  likeCount?: number;
}

export const TechniqueCard = ({
  id,
  name,
  description,
  position,
  level,
  type,
  imageUrl,
  onSave,
  saved = false,
  hasNotes = false,
  hasVideos = false,
  isEditable = false,
  onEdit,
  isUserCreated = false,
  isHidden = false,
  onHideToggle,
  isLiked = false,
  onLikeToggle,
  likeCount = 0,
}: TechniqueCardProps) => {
  const { 
    isLiked: localIsLiked,
    isLiking,
    likeAnimation,
    likeCount: localLikeCount,
    handleLikeToggle
  } = useTechniqueLikes(id, true, isLiked, likeCount);
  
  const { 
    isHidden: localIsHidden,
    isHiding,
    handleHideToggle
  } = useTechniqueVisibility(id, isHidden);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave) onSave(e);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) onEdit(e);
  };

  const handleTechniqueLikeToggle = (e: React.MouseEvent) => {
    handleLikeToggle(e, onLikeToggle);
  };

  const handleTechniqueHideToggle = (e: React.MouseEvent) => {
    handleHideToggle(e, onHideToggle);
  };

  const displayLikeCount = likeCount > 0 ? likeCount : localLikeCount;

  return (
    <Card className="h-full transition-all hover:shadow-md cursor-pointer">
      <CardHeader className="space-y-1 pb-2 px-3 sm:px-6 pt-3 sm:pt-6 relative">
        <div className="flex justify-between items-start">
          <CardTitle className="text-base sm:text-lg font-medium line-clamp-2 text-left">{name}</CardTitle>
          
          {onSave && (
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0 ml-1" 
              onClick={handleSave}
            >
              <BookmarkIcon 
                className={cn("h-3 w-3 sm:h-4 sm:w-4", saved ? "fill-current" : "fill-none")} 
              />
            </Button>
          )}
        </div>
        
        <TechniqueCardBadges 
          position={position}
          type={type}
          level={level}
          isUserCreated={isUserCreated}
        />
      </CardHeader>
      
      <CardContent className="px-3 sm:px-6 py-2">
        <TechniqueCardImage 
          imageUrl={imageUrl} 
          name={name} 
          likeCount={displayLikeCount}
          isLiked={onLikeToggle ? isLiked : localIsLiked}
          isHidden={onHideToggle ? isHidden : localIsHidden}
          likeAnimation={likeAnimation}
          onHideToggle={isUserCreated ? handleTechniqueHideToggle : undefined}
        />
        
        <CardDescription className="line-clamp-3 mt-2 text-xs sm:text-sm text-left">
          {description}
        </CardDescription>
      </CardContent>
      
      <CardFooter className="pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
        <TechniqueCardFooter 
          hasNotes={hasNotes}
          hasVideos={hasVideos}
          isUserCreated={isUserCreated}
          isEditable={isEditable}
          isLiked={onLikeToggle ? isLiked : localIsLiked}
          isHidden={onHideToggle ? isHidden : localIsHidden}
          likeCount={displayLikeCount}
          likeAnimation={likeAnimation}
          isLiking={isLiking}
          isHiding={isHiding}
          onLikeToggle={handleTechniqueLikeToggle}
          onHideToggle={isUserCreated ? handleTechniqueHideToggle : undefined}
          onEdit={handleEdit}
        />
      </CardFooter>
    </Card>
  );
};
